import React from 'react';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

interface Application {
  id: string;
  application_number: string;
  debtor_id: string;
  requested_amount: number;
  total_score: number;
}

interface ApprovalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  application: Application | null;
  action: 'approve' | 'reject' | null;
  notes: string;
  onNotesChange: (notes: string) => void;
  onConfirm: () => void;
  processing: boolean;
  getDebtorName: (id: string) => string;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(value || 0);
};

const getScoreColor = (score: number) => {
  if (score >= 70) return 'text-green-600';
  if (score >= 50) return 'text-amber-600';
  return 'text-red-600';
};

export default function ApprovalDialog({
  isOpen,
  onClose,
  application,
  action,
  notes,
  onNotesChange,
  onConfirm,
  processing,
  getDebtorName,
}: ApprovalDialogProps) {
  if (!isOpen || !application) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 animate-fadeIn">
        {/* Header */}
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            {action === 'approve' ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600" />
                Setujui Aplikasi Kredit
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-red-600" />
                Tolak Aplikasi Kredit
              </>
            )}
          </h3>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Application Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">No. Aplikasi</p>
                <p className="font-medium">{application.application_number}</p>
              </div>
              <div>
                <p className="text-gray-500">Debitur</p>
                <p className="font-medium">{getDebtorName(application.debtor_id)}</p>
              </div>
              <div>
                <p className="text-gray-500">Plafon</p>
                <p className="font-medium">{formatCurrency(application.requested_amount)}</p>
              </div>
              <div>
                <p className="text-gray-500">Skor</p>
                <p className={`font-bold ${getScoreColor(application.total_score)}`}>
                  {application.total_score}
                </p>
              </div>
            </div>
          </div>

          {/* Warning for rejection */}
          {action === 'reject' && (
            <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-red-800">Perhatian</p>
                <p className="text-red-600">Aplikasi yang ditolak tidak dapat diubah kembali.</p>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Catatan {action === 'reject' ? '(Alasan Penolakan)' : ''}
            </label>
            <textarea
              value={notes}
              onChange={e => onNotesChange(e.target.value)}
              placeholder={
                action === 'reject'
                  ? 'Jelaskan alasan penolakan...'
                  : 'Catatan tambahan (opsional)...'
              }
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-300"
          >
            Batal
          </button>
          <button
            onClick={onConfirm}
            disabled={processing || (action === 'reject' && !notes)}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
              action === 'approve'
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {processing ? 'Memproses...' : action === 'approve' ? 'Setujui' : 'Tolak'}
          </button>
        </div>
      </div>
    </div>
  );
}
